import React, { useState, useEffect } from "react";
import {
  Box,
  SimpleGrid,
  Text,
  Heading,
  VStack,
  HStack,
  Icon,
  Badge,
  Avatar,
  Container,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Flex,
  Divider,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import {
  FiHome,
  FiMapPin,
  FiCalendar,
  FiUser,
  FiPhone,
  FiMail,
  FiBuilding,
  FiCheckCircle,
  FiClock,
  FiTrendingUp,
} from "react-icons/fi";
import { MdApartment, MdLocationCity } from "react-icons/md";
import { BsBuilding } from "react-icons/bs";
import client from "../../../../helpers/Client";
import { useLoaderData, useRevalidator } from "react-router-dom";
import urlToId from "../../../../helpers/UrlToId";
import Link from "../../../../components/Link";
import { translateProjectKind } from "../../../../shared/libs/translate";
import { ModernTable } from "../../../../components/tables";
import MapBoxField from "../../../../components/map/MapBoxField";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Modern Info Card Component
function ModernInfoCard({ title, value, icon, color = "blue", delay = 0 }) {
  const cardBg = "rgba(255, 255, 255, 0.8)";
  const borderColor = "gray.200";
  const textColor = "gray.600";

  return (
    <Card
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "lg",
        borderColor: `${color}.300`,
      }}
      animation={`${slideUp} 0.6s ease-out ${delay}s both`}
    >
      <CardBody p={4}>
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Icon as={icon} boxSize={5} color={`${color}.500`} />
          </HStack>
          <VStack align="stretch" spacing={1}>
            <Text fontSize="xs" color={textColor} fontWeight="medium">
              {title}
            </Text>
            <Text fontSize="sm" fontWeight="semibold" color={`${color}.600`}>
              {value || "--"}
            </Text>
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );
}

// Project Stats Component
function ProjectStats({ totalUnits, openTickets, closedTickets }) {
  const cardBg = "rgba(255, 255, 255, 0.8)";
  const borderColor = "gray.200";

  const completionRate = totalUnits > 0 ? Math.round((closedTickets / (openTickets + closedTickets)) * 100) : 0;

  return (
    <Card
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      animation={`${fadeIn} 0.8s ease-out 0.2s both`}
    >
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <Heading size="md">Proje İstatistikleri</Heading>
          <Icon as={FiTrendingUp} color="blue.500" />
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        <SimpleGrid columns={4} spacing={4}>
          <Stat textAlign="center">
            <StatLabel fontSize="xs">Toplam Ünite</StatLabel>
            <StatNumber fontSize="2xl" color="blue.500">{totalUnits}</StatNumber>
          </Stat>
          <Stat textAlign="center">
            <StatLabel fontSize="xs">Açık İş Emirleri</StatLabel>
            <StatNumber fontSize="2xl" color="orange.500">{openTickets}</StatNumber>
          </Stat>
          <Stat textAlign="center">
            <StatLabel fontSize="xs">Tamamlanan</StatLabel>
            <StatNumber fontSize="2xl" color="green.500">{closedTickets}</StatNumber>
          </Stat>
          <Stat textAlign="center">
            <StatLabel fontSize="xs">Tamamlanma</StatLabel>
            <StatNumber fontSize="lg" color="purple.500">{completionRate}%</StatNumber>
            <Progress value={completionRate} colorScheme="purple" size="sm" mt={2} borderRadius="full" />
          </Stat>
        </SimpleGrid>
      </CardBody>
    </Card>
  );
}

export async function projectLoader({ params }) {
  const response = await client.get(`/units/?project=${params.projectId}`);
  return [response.data];
}

export default function ProjectDetail() {
  const [units] = useLoaderData();
  const revalidator = useRevalidator();
  const project = units[0]?.project;
  const authorizedDisclosure = useDisclosure();
  const extraDisclosure = useDisclosure();

  // Define color mode values at component level
  const cardBg = "rgba(255, 255, 255, 0.9)";
  const borderColor = "gray.200";

  const bgGradient = "linear(135deg, blue.50 0%, purple.50 25%, pink.50 50%, orange.50 75%, yellow.50 100%)";

  // Calculate stats
  const totalUnits = units.length;
  const totalOpenTickets = units.reduce((sum, unit) => sum + unit.num_of_open_tickets, 0);
  const totalClosedTickets = units.reduce((sum, unit) => sum + unit.num_of_closed_tickets, 0);

  // Table columns for units
  const unitColumns = [
    {
      title: "Ünite No",
      dataIndex: "apt_num",
      render: (text, record) => (
        <Link to={`/unit/${urlToId(record.url)}/`} fontWeight="semibold" color="blue.600">
          <HStack spacing={2}>
            <Icon as={FiHome} />
            <Text>{text}</Text>
          </HStack>
        </Link>
      ),
    },
    {
      title: "Açık İş Emirleri",
      dataIndex: "num_of_open_tickets",
      render: (text) => (
        <Badge colorScheme={text > 0 ? "orange" : "gray"} variant="subtle" borderRadius="full">
          <HStack spacing={1}>
            <Icon as={FiClock} boxSize={3} />
            <Text>{text}</Text>
          </HStack>
        </Badge>
      ),
    },
    {
      title: "Tamamlanan İş Emirleri",
      dataIndex: "num_of_closed_tickets",
      render: (text) => (
        <Badge colorScheme={text > 0 ? "green" : "gray"} variant="subtle" borderRadius="full">
          <HStack spacing={1}>
            <Icon as={FiCheckCircle} boxSize={3} />
            <Text>{text}</Text>
          </HStack>
        </Badge>
      ),
    },
    {
      title: "Toplam İş Emirleri",
      dataIndex: "total_tickets",
      render: (text, record) => {
        const total = record.num_of_open_tickets + record.num_of_closed_tickets;
        return (
          <Text fontSize="sm" fontWeight="medium">
            {total}
          </Text>
        );
      },
    },
    {
      title: "Tamamlanma Oranı",
      dataIndex: "completion_rate",
      render: (text, record) => {
        const total = record.num_of_open_tickets + record.num_of_closed_tickets;
        const rate = total > 0 ? Math.round((record.num_of_closed_tickets / total) * 100) : 0;
        return (
          <VStack spacing={1} align="stretch">
            <Text fontSize="sm" fontWeight="medium">{rate}%</Text>
            <Progress value={rate} colorScheme={rate === 100 ? "green" : rate > 50 ? "blue" : "orange"} size="sm" borderRadius="full" />
          </VStack>
        );
      },
    },
  ];

  if (!project) {
    return (
      <Box minH="100vh" bgGradient={bgGradient} display="flex" alignItems="center" justifyContent="center">
        <Text>Proje bulunamadı</Text>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
    >
      {/* Background Pattern */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        opacity={0.1}
        backgroundImage="radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.2) 0%, transparent 50%)"
        pointerEvents="none"
      />

      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Header Section */}
          <Card
            bg={cardBg}
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor={borderColor}
            borderRadius="2xl"
            overflow="hidden"
            animation={`${fadeIn} 0.6s ease-out`}
          >
            <CardBody p={8}>
              <Flex direction={{ base: "column", lg: "row" }} gap={8} align="center">
                {/* Project Icon */}
                <VStack spacing={4}>
                  <Avatar
                    size="2xl"
                    icon={<Icon as={BsBuilding} fontSize="2xl" />}
                    bg="blue.500"
                    color="white"
                    animation={`${pulse} 3s ease-in-out infinite`}
                  />
                  <VStack spacing={1} textAlign="center">
                    <Heading size="lg" color="blue.600">
                      {project.name}
                    </Heading>
                    <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={3} py={1}>
                      {translateProjectKind(project.kind)}
                    </Badge>
                  </VStack>
                </VStack>

                {/* Project Info Cards Grid */}
                <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4} flex={1}>
                  <ModernInfoCard
                    title="Bölge"
                    value={project.district_info?.name}
                    icon={MdLocationCity}
                    color="purple"
                    delay={0.1}
                  />
                  <ModernInfoCard
                    title="Teslimat Tarihi"
                    value={project.delivery_date}
                    icon={FiCalendar}
                    color="green"
                    delay={0.2}
                  />
                  <ModernInfoCard
                    title="Yetkili"
                    value={project.contact_name}
                    icon={FiUser}
                    color="orange"
                    delay={0.3}
                  />
                  <ModernInfoCard
                    title="Telefon"
                    value={project.contact_phone}
                    icon={FiPhone}
                    color="teal"
                    delay={0.4}
                  />
                  <ModernInfoCard
                    title="E-posta"
                    value={project.contact_email}
                    icon={FiMail}
                    color="cyan"
                    delay={0.5}
                  />
                  <ModernInfoCard
                    title="Adres"
                    value={project.address}
                    icon={FiMapPin}
                    color="pink"
                    delay={0.6}
                  />
                </SimpleGrid>
              </Flex>
            </CardBody>
          </Card>

          {/* Address Map Visualization */}
          <Card
            bg={cardBg}
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor={borderColor}
            borderRadius="2xl"
            overflow="hidden"
            animation={`${fadeIn} 0.7s ease-out 0.2s both`}
          >
            <CardHeader>
              <HStack spacing={3}>
                <Icon as={FiMapPin} color="blue.500" />
                <Heading size="sm">Proje Konumu</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <MapBoxField value={project.address} onChangeAddress={() => { }} />
            </CardBody>
          </Card>

          {/* Stats Section */}
          <ProjectStats
            totalUnits={totalUnits}
            openTickets={totalOpenTickets}
            closedTickets={totalClosedTickets}
          />

          {/* Units Table */}
          <Card
            bg={cardBg}
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor={borderColor}
            borderRadius="2xl"
            overflow="hidden"
            animation={`${fadeIn} 1s ease-out 0.4s both`}
          >
            <CardHeader>
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Icon as={MdApartment} color="blue.500" boxSize={6} />
                  <Heading size="md">Ünite Listesi ({totalUnits})</Heading>
                </HStack>
                <HStack>
                  <Button colorScheme="blue" variant="outline" onClick={authorizedDisclosure.onOpen}>Yetkili Ata</Button>
                  <Button colorScheme="purple" variant="solid" onClick={extraDisclosure.onOpen}>Ek Bilgiler</Button>
                </HStack>
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              <ModernTable
                data={units}
                columns={unitColumns}
                variant="minimal"
                size="md"
                showSearch={true}
                showPagination={true}
                pageSize={10}
              />
            </CardBody>
          </Card>

          {/* Authorized Assign Modal */}
          <AuthorizedModal isOpen={authorizedDisclosure.isOpen} onClose={authorizedDisclosure.onClose} project={project} onSaved={() => revalidator.revalidate()} />
          <ExtraInfoModal isOpen={extraDisclosure.isOpen} onClose={extraDisclosure.onClose} project={project} onSaved={() => revalidator.revalidate()} />
        </VStack>
      </Container>
    </Box>
  );
}

function AuthorizedModal({ project, isOpen, onClose, onSaved }) {
  const toast = useToast();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [smsSessionToken, setSmsSessionToken] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [smsSent, setSmsSent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const [fn = "", ln = ""] = (project?.contact_name || "").split(" ");
      setFirstName(fn);
      setLastName(ln);
      setPhone(project?.contact_phone || "");
    }
  }, [isOpen, project]);

  const handleSave = () => {
    if (!firstName.trim() || !lastName.trim() || !phone.trim()) {
      toast({ title: "Eksik bilgi", description: "Ad, soyad ve telefon zorunlu", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    const contact_name = `${firstName.trim()} ${lastName.trim()}`;
    const projectId = project?.url ? Number(project.url.split("/").filter(Boolean).pop()) : null;
    if (!projectId) {
      toast({ title: "Hata", description: "Proje ID bulunamadı", status: "error", duration: 3000, isClosable: true });
      return;
    }
    // Verify SMS code, then patch project contact
    client.post('/public/phones/verify/', {
      phone_number: phone,
      security_code: smsCode,
      session_token: smsSessionToken || undefined,
    })
      .then(() => client.patch(`/projects/${projectId}/`, { contact_name, contact_phone: phone }))
      .then(() => {
        toast({ title: "Yetkili güncellendi", status: "success", duration: 3000, isClosable: true });
        onClose();
        onSaved && onSaved();
      })
      .catch(() => {
        toast({ title: "Hata", description: "Doğrulama veya atama başarısız", status: "error", duration: 3000, isClosable: true });
      });
  };

  return (
    <>
      {/* Hidden button trigger via function reference */}
      {/* Modal controlled by parent disclosure */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={false}>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.200">
          <ModalHeader>Proje Yetkilisi Ata</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Ad</FormLabel>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Ad" />
              </FormControl>
              <FormControl>
                <FormLabel>Soyad</FormLabel>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Soyad" />
              </FormControl>
              <FormControl>
                <FormLabel>Telefon</FormLabel>
                <HStack>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefon" />
                  <Button onClick={() => {
                    if (!phone.trim()) {
                      toast({ title: 'Telefon gerekli', status: 'warning', duration: 3000, isClosable: true });
                      return;
                    }
                    client.post('/public/phones/register/', { phone_number: phone })
                      .then(res => {
                        setSmsSessionToken(res.data?.session_token || '');
                        setSmsSent(true);
                        toast({ title: 'SMS gönderildi', status: 'info', duration: 3000, isClosable: true });
                      })
                      .catch(() => {
                        toast({ title: 'SMS gönderilemedi', status: 'error', duration: 3000, isClosable: true });
                      });
                  }} colorScheme="blue">SMS Gönder</Button>
                </HStack>
              </FormControl>
              {smsSent && (
                <FormControl>
                  <FormLabel>SMS Kod</FormLabel>
                  <Input value={smsCode} onChange={(e) => setSmsCode(e.target.value)} placeholder="Güvenlik Kodu" />
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button colorScheme="blue" onClick={handleSave}>Kaydet</Button>
              <Button variant="ghost" onClick={onClose}>Vazgeç</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function ExtraInfoModal({ project, isOpen, onClose, onSaved }) {
  const toast = useToast();
  const [warrantyMonths, setWarrantyMonths] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [extraInfo, setExtraInfo] = useState("");

  useEffect(() => {
    if (isOpen) {
      setWarrantyMonths(project?.warranty_months ?? "");
      setDeliveryDate(project?.delivery_date ?? "");
      setExtraInfo(project?.extra_info ?? "");
    }
  }, [isOpen, project]);

  const handleSave = () => {
    const projectId = project?.url ? Number(project.url.split("/").filter(Boolean).pop()) : null;
    if (!projectId) {
      toast({ title: "Hata", description: "Proje ID bulunamadı", status: "error", duration: 3000, isClosable: true });
      return;
    }
    client.patch(`/projects/${projectId}/`, {
      warranty_months: warrantyMonths !== "" ? Number(warrantyMonths) : null,
      delivery_date: deliveryDate || null,
      extra_info: extraInfo || null,
    }).then(() => {
      toast({ title: "Ek bilgiler güncellendi", status: "success", duration: 3000, isClosable: true });
      onClose();
      onSaved && onSaved();
    }).catch(() => {
      toast({ title: "Hata", description: "Ek bilgiler kaydedilemedi", status: "error", duration: 3000, isClosable: true });
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={false}>
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.200">
        <ModalHeader>Proje Ek Bilgileri</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Garanti Süresi (Ay)</FormLabel>
              <Input type="number" value={warrantyMonths} onChange={(e) => setWarrantyMonths(e.target.value)} placeholder="Örn: 24" />
            </FormControl>
            <FormControl>
              <FormLabel>Teslimat Tarihi</FormLabel>
              <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Ek Notlar</FormLabel>
              <Textarea value={extraInfo} onChange={(e) => setExtraInfo(e.target.value)} rows={3} />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button colorScheme="purple" onClick={handleSave}>Kaydet</Button>
            <Button variant="ghost" onClick={onClose}>Vazgeç</Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
